/**
 * CV file rules, shared by every surface that accepts or serves a CV (#840).
 *
 * These constants used to be duplicated: the accepted MIME types lived in
 * `CvUpload.tsx` (public signup) and a near-identical mime→extension map in
 * `consultant-applications.ts`. Both now import from here, so widening the
 * accepted formats is a one-line change rather than a grep.
 */

export const ACCEPTED_CV_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

/** Value for a file input's `accept` attribute. */
export const ACCEPTED_CV_EXTENSIONS = ".pdf,.docx";

export const MAX_CV_SIZE_MB = 10;

/**
 * Extension to append when a download's `Content-Disposition` filename is
 * unavailable (the header is not always exposed to the browser), keyed by the
 * blob's MIME type. Mirrors ACCEPTED_CV_MIME_TYPES.
 */
export const CV_EXTENSION_BY_MIME: Record<string, string> = {
  "application/pdf": ".pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
};

/**
 * Client-side guard run before any upload: returns null when the file is
 * acceptable, otherwise a user-facing reason. A missing file is treated as a
 * validation failure so callers can pass `input.files?.[0]` straight in.
 */
export const validateCvFile = (file: File | null | undefined): string | null => {
  if (!file) {
    return "Please choose a file.";
  }
  if (!ACCEPTED_CV_MIME_TYPES.includes(file.type)) {
    return "Only PDF and DOCX files are accepted.";
  }
  if (file.size > MAX_CV_SIZE_MB * 1024 * 1024) {
    return `File size must not exceed ${MAX_CV_SIZE_MB}MB.`;
  }
  return null;
};
